// src/components/PrivacyModal.js - 개인정보처리방침 모달
import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { Button } from "./ui/button";

const PrivacyModal = ({ isOpen, onClose, onAgree }) => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [canAgree, setCanAgree] = useState(false);
  const scrollContainerRef = useRef(null);
  const startTimeRef = useRef(null);

  // 모달이 열릴 때 시간 추적 시작
  useEffect(() => {
    if (isOpen) {
      startTimeRef.current = Date.now();
      setScrollProgress(0);
      setHasScrolledToBottom(false);
      setTimeElapsed(0);
      setCanAgree(false);

      // 1초마다 경과 시간 업데이트
      const timer = setInterval(() => {
        if (startTimeRef.current) {
          const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
          setTimeElapsed(elapsed);
        }
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isOpen]);

  // 동의 버튼 활성화 조건 체크
  useEffect(() => {
    // 조건: 3초 이상 경과 AND 80% 이상 스크롤
    if (timeElapsed >= 3 && scrollProgress >= 80) {
      setCanAgree(true);
    }
  }, [timeElapsed, scrollProgress]);

  // 스크롤 이벤트 핸들러
  const handleScroll = () => {
    if (!scrollContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    const scrollPercentage = ((scrollTop + clientHeight) / scrollHeight) * 100;

    setScrollProgress(Math.min(Math.round(scrollPercentage), 100));

    // 90% 이상 스크롤하면 "맨 아래까지 읽음" 처리
    if (scrollPercentage >= 90) {
      setHasScrolledToBottom(true);
    }
  };

  const handleAgree = () => {
    if (canAgree) {
      onAgree();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[95vh] sm:max-h-[90vh] flex flex-col">
        {/* 헤더 */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">개인정보처리방침</h2>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">블루북 English 개인정보 보호정책</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 flex-shrink-0"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        {/* 읽기 진행 상태 표시 */}
        <div className="px-3 sm:px-6 py-2 sm:py-3 bg-blue-50 border-b border-blue-100">
          <div className="flex items-center justify-between text-xs sm:text-sm mb-2 gap-2">
            <span className="text-gray-700 whitespace-nowrap">
              {canAgree ? (
                <span className="text-green-600 font-semibold">✅ 동의 가능</span>
              ) : (
                <span className="text-gray-600">
                  ⏱️ {timeElapsed}초/3초
                </span>
              )}
            </span>
            <span className="text-gray-700 whitespace-nowrap">
              📜 {scrollProgress}% {hasScrolledToBottom && '✅'}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${
                scrollProgress >= 80 ? 'bg-green-500' : 'bg-blue-500'
              }`}
              style={{ width: `${scrollProgress}%` }}
            ></div>
          </div>
          <p className="text-[10px] sm:text-xs text-gray-500 mt-1">
            💡 개인정보처리방침을 끝까지 읽어주세요 (80% 스크롤 + 3초 경과)
          </p>
        </div>

        {/* 요약 정보 */}
        <div className="px-3 sm:px-6 py-3 sm:py-4 bg-green-50 border-b border-green-200">
          <h3 className="text-xs sm:text-sm font-bold text-green-900 mb-2">🔒 핵심 요약</h3>
          <ul className="text-[10px] sm:text-xs text-green-800 space-y-1">
            <li>• 이메일, 비밀번호, 결제 정보를 수집합니다.</li>
            <li>• 수집된 정보는 서비스 제공 및 결제 처리에만 사용됩니다.</li>
            <li>• 회원 탈퇴 시 즉시 파기됩니다 (법령상 보관 의무 제외).</li>
            <li>• 토스페이먼츠(PG사)에 결제 정보가 제공됩니다.</li>
            <li>• 언제든지 개인정보 열람, 수정, 삭제를 요청할 수 있습니다.</li>
          </ul>
        </div>

        {/* 약관 내용 (스크롤 가능) */}
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto px-3 sm:px-6 py-4 sm:py-6"
        >
          <div className="prose prose-sm max-w-none">
            {/* 제1조 (개인정보의 수집 항목) */}
            <section className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">제1조 (개인정보의 수집 항목)</h3>
              <p className="text-gray-700 mb-2">회사는 서비스 제공을 위해 다음과 같은 개인정보를 수집합니다:</p>

              <div className="ml-4 space-y-4">
                <div className="bg-gray-50 p-4 rounded">
                  <p className="font-semibold text-gray-900 mb-2">1. 필수 수집 항목 (회원가입 시)</p>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    <li>이메일 주소</li>
                    <li>비밀번호 (암호화 저장)</li>
                    <li>이름</li>
                  </ul>
                </div>

                <div className="bg-gray-50 p-4 rounded">
                  <p className="font-semibold text-gray-900 mb-2">2. 자동 수집 항목 (서비스 이용 시)</p>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    <li>서비스 이용 기록</li>
                    <li>접속 로그</li>
                    <li>쿠키</li>
                    <li>접속 IP 정보</li>
                  </ul>
                </div>

                <div className="bg-gray-50 p-4 rounded">
                  <p className="font-semibold text-gray-900 mb-2">3. 결제 정보 (유료 구독 시)</p>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    <li>결제 수단 정보 (카드번호 일부 등)</li>
                    <li>구매 내역</li>
                    <li>결제 일시</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* 제2조 (개인정보의 수집 및 이용 목적) */}
            <section className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">제2조 (개인정보의 수집 및 이용 목적)</h3>
              <p className="text-gray-700 mb-2">회사는 수집한 개인정보를 다음의 목적을 위해 활용합니다:</p>
              <div className="space-y-2 text-gray-700 ml-4">
                <p><span className="font-semibold">1.</span> 서비스 제공 및 운영: 회원 관리, 서비스 이용에 따른 본인 식별·인증</p>
                <p><span className="font-semibold">2.</span> 구매 및 결제: 구독 서비스 제공, 결제 처리, 영수증 발행</p>
                <p><span className="font-semibold">3.</span> 고객 지원: 문의 응대, 불만 처리, 공지사항 전달</p>
                <p><span className="font-semibold">4.</span> 서비스 개선: 신규 서비스 개발, 통계 분석, 맞춤형 서비스 제공</p>
                <p><span className="font-semibold">5.</span> 부정 이용 방지: 부정 사용 방지 및 비인가 사용 방지</p>
              </div>
            </section>

            {/* 제3조 (개인정보의 보유 및 이용 기간) */}
            <section className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">제3조 (개인정보의 보유 및 이용 기간)</h3>
              <div className="space-y-2 text-gray-700">
                <p><span className="font-semibold">1.</span> 회사는 회원이 회원 자격을 유지하고 있는 동안 개인정보를 보유 및 이용합니다.</p>
                <p><span className="font-semibold">2.</span> 회원 탈퇴 시 개인정보는 즉시 파기됩니다.</p>
                <p><span className="font-semibold">3.</span> 단, 다음의 경우에는 해당 기간 동안 보관합니다:</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li><strong>전자상거래법:</strong> 계약 또는 청약철회 기록 (5년), 대금결제 기록 (5년), 소비자 불만 기록 (3년)</li>
                  <li><strong>통신비밀보호법:</strong> 접속 로그 기록 (3개월)</li>
                  <li><strong>부정 이용 방지:</strong> 부정 이용 기록 (1년)</li>
                </ul>
              </div>
            </section>

            {/* 제4조 (개인정보의 파기 절차 및 방법) */}
            <section className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">제4조 (개인정보의 파기 절차 및 방법)</h3>
              <div className="space-y-2 text-gray-700">
                <p><span className="font-semibold">1. 파기 절차</span></p>
                <p className="ml-4">회원이 입력한 정보는 목적 달성 후 별도의 DB에 옮겨져 내부 방침 및 기타 관련 법령에 따라 일정 기간 저장된 후 파기됩니다.</p>

                <p className="mt-4"><span className="font-semibold">2. 파기 방법</span></p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>전자적 파일 형태: 복구 불가능한 방법으로 영구 삭제</li>
                  <li>종이 문서: 분쇄기로 분쇄하거나 소각</li>
                </ul>
              </div>
            </section>

            {/* 제5조 (개인정보의 제3자 제공) */}
            <section className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">제5조 (개인정보의 제3자 제공)</h3>
              <p className="text-gray-700 mb-3">회사는 원칙적으로 회원의 개인정보를 외부에 제공하지 않습니다. 다만, 아래의 경우는 예외로 합니다:</p>

              <div className="ml-4 bg-gray-50 p-4 rounded">
                <p><span className="font-medium">제공받는 자:</span> 토스페이먼츠 (PG사)</p>
                <p><span className="font-medium">제공 항목:</span> 이메일, 구매 정보</p>
                <p><span className="font-medium">제공 목적:</span> 결제 처리</p>
                <p><span className="font-medium">보유 기간:</span> 결제 완료 후 5년</p>
              </div>
            </section>

            {/* 제6조 (개인정보 처리의 위탁) */}
            <section className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">제6조 (개인정보 처리의 위탁)</h3>
              <p className="text-gray-700 mb-3">회사는 서비스 제공을 위해 아래와 같이 개인정보 처리 업무를 외부 전문업체에 위탁하고 있습니다:</p>

              <div className="space-y-3 ml-4">
                <div className="bg-gray-50 p-4 rounded">
                  <p className="font-semibold text-gray-900 mb-2">1. Google Firebase</p>
                  <p className="text-sm text-gray-700">위탁 업무: 서버 호스팅, 데이터베이스 관리, 사용자 인증</p>
                </div>

                <div className="bg-gray-50 p-4 rounded">
                  <p className="font-semibold text-gray-900 mb-2">2. 토스페이먼츠</p>
                  <p className="text-sm text-gray-700">위탁 업무: 결제 대행 서비스</p>
                </div>
              </div>
            </section>

            {/* 제7조 (정보주체의 권리·의무 및 행사 방법) */}
            <section className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">제7조 (정보주체의 권리·의무 및 행사 방법)</h3>
              <div className="space-y-2 text-gray-700">
                <p>회원은 언제든지 다음과 같은 권리를 행사할 수 있습니다:</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li><strong>개인정보 열람 요구</strong></li>
                  <li><strong>오류 등이 있을 경우 정정 요구</strong></li>
                  <li><strong>삭제 요구</strong></li>
                  <li><strong>처리 정지 요구</strong></li>
                </ul>
                <p className="mt-3">권리 행사는 서면, 전화, 이메일 등을 통해 하실 수 있으며, 회사는 지체없이 조치하겠습니다.</p>
                <p className="mt-2">문의: <a href="mailto:support@bluebookgo.com" className="text-blue-600 hover:underline">support@bluebookgo.com</a></p>
              </div>
            </section>

            {/* 제8조 (쿠키의 운영 및 거부) */}
            <section className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">제8조 (쿠키의 운영 및 거부)</h3>
              <div className="space-y-2 text-gray-700">
                <p><span className="font-semibold">1.</span> 회사는 이용자에게 개별적인 맞춤 서비스를 제공하기 위해 쿠키를 사용합니다.</p>
                <p><span className="font-semibold">2.</span> 쿠키는 웹사이트 운영에 이용되는 서버가 사용자의 브라우저에 보내는 아주 작은 텍스트 파일로 사용자의 컴퓨터에 저장됩니다.</p>
                <p><span className="font-semibold">3.</span> 쿠키 설치/운영 거부 방법:</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>웹브라우저 상단의 도구 &gt; 인터넷 옵션 &gt; 개인정보 메뉴에서 설정 가능</li>
                  <li>단, 쿠키 저장을 거부할 경우 맞춤형 서비스 이용에 어려움이 발생할 수 있습니다</li>
                </ul>
              </div>
            </section>

            {/* 제9조 (개인정보 보호를 위한 기술적·관리적 대책) */}
            <section className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">제9조 (개인정보 보호를 위한 기술적·관리적 대책)</h3>
              <div className="space-y-3 text-gray-700">
                <div>
                  <p className="font-semibold">1. 기술적 대책</p>
                  <ul className="list-disc list-inside ml-4 space-y-1 mt-1">
                    <li>개인정보는 암호화되어 저장 및 관리됩니다</li>
                    <li>비밀번호는 단방향 암호화되어 저장됩니다</li>
                    <li>해킹 등에 대비한 보안프로그램을 운영합니다</li>
                    <li>SSL(Secure Socket Layer) 암호화 통신을 사용합니다</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold">2. 관리적 대책</p>
                  <ul className="list-disc list-inside ml-4 space-y-1 mt-1">
                    <li>개인정보 취급 직원을 최소한으로 제한합니다</li>
                    <li>개인정보 처리자에 대한 정기적인 교육을 실시합니다</li>
                    <li>내부 관리 계획을 수립하고 시행합니다</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* 제10조 (개인정보 보호책임자) */}
            <section className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">제10조 (개인정보 보호책임자)</h3>
              <p className="text-gray-700 mb-3">회사는 개인정보 처리에 관한 업무를 총괄해서 책임지고, 개인정보 처리와 관련한 정보주체의 불만처리 및 피해구제를 위하여 아래와 같이 개인정보 보호책임자를 지정하고 있습니다:</p>

              <div className="ml-4 bg-blue-50 p-4 rounded border border-blue-200">
                <p className="font-semibold text-gray-900 mb-2">개인정보 보호책임자</p>
                <p className="text-sm text-gray-700">이메일: <a href="mailto:support@bluebookgo.com" className="text-blue-600 hover:underline font-medium">support@bluebookgo.com</a></p>
                <p className="text-xs text-gray-600 mt-2">※ 개인정보 침해에 대한 신고나 상담이 필요하신 경우 위 연락처로 문의해 주시기 바랍니다.</p>
              </div>
            </section>

            {/* 제11조 (개인정보처리방침의 변경) */}
            <section className="mb-6 pb-8 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 mb-3">제11조 (개인정보처리방침의 변경)</h3>
              <div className="space-y-2 text-gray-700">
                <p><span className="font-semibold">1.</span> 이 개인정보처리방침은 2025년 1월 1일부터 적용됩니다.</p>
                <p><span className="font-semibold">2.</span> 법령·정책 또는 보안기술의 변경에 따라 내용의 추가·삭제 및 수정이 있을 경우 변경사항의 시행 7일 전부터 공지사항을 통해 고지할 것입니다.</p>
              </div>
            </section>

            {/* 문의 정보 */}
            <section className="mb-6 bg-gray-50 p-4 rounded-lg">
              <h3 className="text-sm font-bold text-gray-900 mb-2">📧 개인정보 관련 문의</h3>
              <p className="text-sm text-gray-700">
                개인정보 처리와 관련한 문의사항이 있으시면 아래로 연락주세요:
              </p>
              <p className="text-sm text-gray-700 mt-2">
                이메일: <a href="mailto:support@bluebookgo.com" className="text-blue-600 hover:underline">support@bluebookgo.com</a>
              </p>
              <p className="text-xs text-gray-500 mt-3">
                또는 개인정보침해 신고센터, 대검찰청 사이버범죄수사단, 경찰청 사이버안전국 등에 신고하실 수 있습니다.
              </p>
            </section>

            {/* 스크롤 끝 표시 */}
            <div className="text-center py-4 text-gray-400 text-sm">
              - 개인정보처리방침의 끝 -
            </div>
          </div>
        </div>

        {/* 하단 버튼 */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-6 border-t border-gray-200 bg-gray-50 gap-2">
          <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
            {!canAgree && (
              <span>💡 개인정보처리방침을 끝까지 읽어주세요</span>
            )}
          </div>
          <div className="flex gap-2 sm:gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 sm:flex-none text-sm"
            >
              닫기
            </Button>
            <Button
              onClick={handleAgree}
              disabled={!canAgree}
              className={`flex-1 sm:flex-none sm:min-w-[120px] text-sm ${
                !canAgree ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {canAgree ? '✅ 동의합니다' : '🔒 읽는 중...'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyModal;
